/*
 * product.js — product page behavior (loaded with defer only where needed)
 * - <media-gallery>: swipe slider, thumbnails, arrows, dots, zoom dialog
 * - <variant-picker>: option selection, price/availability/media/URL updates
 * - <sticky-atc>: mobile sticky add to cart
 * - <delivery-estimate>: real delivery date range from business days
 */
(() => {
  'use strict';

  const theme = window.theme || {};
  const strings = theme.strings || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  /* Gallery ---------------------------------------------------------------- */
  define(
    'media-gallery',
    class extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('[data-slider]');
        this.slides = [...this.querySelectorAll('.gallery__slide')];
        this.thumbs = [...this.querySelectorAll('[data-thumb-index]')];
        this.dots = [...this.querySelectorAll('.gallery__dot')];
        this.prev = this.querySelector('[data-gallery-prev]');
        this.next = this.querySelector('[data-gallery-next]');
        this.zoom = this.querySelector('[data-zoom-dialog]');
        this.index = 0;

        if (this.slides.length > 1) {
          this.slider.addEventListener('scroll', theme.debounce(() => this.onScroll(), 60), { passive: true });
          this.prev?.addEventListener('click', () => this.goTo(this.index - 1));
          this.next?.addEventListener('click', () => this.goTo(this.index + 1));
          this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.thumbIndex))));
          this.slider.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight') this.goTo(this.index + 1);
            if (e.key === 'ArrowLeft') this.goTo(this.index - 1);
          });
        }

        this.querySelectorAll('[data-zoom-index]').forEach((btn) =>
          btn.addEventListener('click', () => this.openZoom(Number(btn.dataset.zoomIndex)))
        );
        this.zoom?.addEventListener('click', (e) => {
          if (e.target.tagName === 'IMG') theme.closeDialog(this.zoom);
        });

        const featured = this.dataset.featuredMedia;
        const start = this.slides.findIndex((s) => s.dataset.mediaId === featured);
        if (start > 0) this.goTo(start, true);
        this.updateUI();
      }

      onScroll() {
        const width = this.slider.clientWidth;
        if (!width) return;
        const i = Math.round(this.slider.scrollLeft / width);
        if (i !== this.index) {
          this.index = Math.max(0, Math.min(i, this.slides.length - 1));
          this.updateUI();
          this.pauseVideos();
        }
      }

      goTo(i, instant = false) {
        if (!this.slides.length) return;
        this.index = Math.max(0, Math.min(i, this.slides.length - 1));
        const slide = this.slides[this.index];
        // Stacked grid layout on desktop: scroll the page instead of the slider.
        if (getComputedStyle(this.slider).display === 'grid') {
          slide.scrollIntoView({ behavior: instant || reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
        } else {
          this.slider.scrollTo({ left: slide.offsetLeft - this.slider.offsetLeft, behavior: instant || reducedMotion ? 'auto' : 'smooth' });
        }
        this.updateUI();
        this.pauseVideos();
      }

      goToMedia(mediaId) {
        const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (i >= 0) this.goTo(i);
      }

      updateUI() {
        this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
        this.thumbs.forEach((t, i) => {
          t.classList.toggle('is-active', i === this.index);
          t.setAttribute('aria-current', i === this.index ? 'true' : 'false');
        });
        this.dots.forEach((d, i) => d.classList.toggle('is-active', i === this.index));
        if (this.prev) this.prev.disabled = this.index === 0;
        if (this.next) this.next.disabled = this.index === this.slides.length - 1;
        const activeThumb = this.thumbs[this.index];
        activeThumb?.parentElement?.parentElement?.scrollTo?.({ left: activeThumb.offsetLeft - 40, behavior: 'smooth' });
      }

      pauseVideos() {
        this.slides.forEach((s, i) => {
          if (i === this.index) return;
          s.querySelectorAll('video').forEach((v) => v.pause());
        });
      }

      openZoom(i) {
        if (!this.zoom) return;
        theme.openDialog(this.zoom);
        const item = this.zoom.querySelector(`[data-zoom-item="${i}"]`);
        requestAnimationFrame(() => item?.scrollIntoView({ block: 'start' }));
      }
    }
  );

  /* Variant picker ------------------------------------------------------------ */
  define(
    'variant-picker',
    class extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('[data-product-section]');
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
        this.fieldsets = [...this.querySelectorAll('fieldset')];
        this.isMainProduct = !!this.section?.querySelector('[data-product-info]');
        this.addEventListener('change', () => this.onChange());
        this.updateAvailability();
      }

      selectedOptions() {
        return this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
      }

      onChange() {
        const selected = this.selectedOptions();
        this.fieldsets.forEach((fs, i) => {
          const label = fs.querySelector('[data-selected-value]');
          if (label) label.textContent = selected[i] || '';
        });
        let variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
        this.updateAvailability();
        this.render(variant);
      }

      // Mark values that have no available variant given the other selected options.
      updateAvailability() {
        const selected = this.selectedOptions();
        this.fieldsets.forEach((fs, position) => {
          fs.querySelectorAll('input').forEach((input) => {
            const available = this.variants.some(
              (v) => v.available && v.options[position] === input.value && v.options.every((o, i) => i === position || i > position || o === selected[i])
            );
            const label = input.nextElementSibling;
            label?.classList.toggle('is-unavailable', !available);
          });
        });
      }

      render(variant) {
        const section = this.section;
        if (!section) return;
        const addButtons = section.querySelectorAll('[data-add-button]');
        const dynamic = section.querySelector('[data-dynamic-checkout]');

        if (!variant) {
          addButtons.forEach((b) => {
            b.disabled = true;
            b.querySelector('[data-add-label]').textContent = strings.unavailable;
          });
          dynamic?.classList.add('is-hidden');
          return;
        }

        section.querySelectorAll('[data-variant-id]').forEach((input) => {
          input.value = variant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        addButtons.forEach((b) => {
          b.disabled = !variant.available;
          b.querySelector('[data-add-label]').textContent = variant.available ? strings.addToCart : strings.soldOut;
        });
        dynamic?.classList.toggle('is-hidden', !variant.available);

        // Price
        section.querySelectorAll('[data-price]').forEach((price) => {
          const current = price.querySelector('[data-price-current]');
          const compare = price.querySelector('[data-price-compare]');
          const badge = price.querySelector('[data-price-badge]');
          const onSale = variant.compare_at_price > variant.price;
          if (current) current.textContent = theme.formatMoney(variant.price);
          price.classList.toggle('price--on-sale', onSale);
          if (compare) {
            compare.hidden = !onSale;
            if (onSale) compare.textContent = theme.formatMoney(variant.compare_at_price);
          }
          if (badge) {
            badge.hidden = !onSale;
            if (onSale) {
              const pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
              badge.textContent = (badge.dataset.template || '-[percent]%').replace('[percent]', pct);
            }
          }
        });

        // Stock message (real inventory only)
        const stock = section.querySelector('[data-stock-note]');
        if (stock) {
          const threshold = Number(stock.dataset.threshold) || 0;
          const low = variant.inventory !== null && variant.inventory > 0 && variant.inventory <= threshold;
          stock.hidden = !variant.available;
          stock.classList.toggle('stock-note--low', low);
          stock.querySelector('[data-stock-text]').textContent = low
            ? strings.lowStock.replace('[count]', variant.inventory)
            : strings.inStock;
        }

        // Sticky bar
        const stickyVariant = section.querySelector('[data-sticky-variant]');
        const stickyPrice = section.querySelector('[data-sticky-price]');
        if (stickyVariant) stickyVariant.textContent = variant.title;
        if (stickyPrice) stickyPrice.textContent = theme.formatMoney(variant.price);

        // Media
        if (variant.featured_media_id) section.querySelector('media-gallery')?.goToMedia(variant.featured_media_id);

        // URL (main product only, so featured product sections don't hijack the URL)
        if (this.isMainProduct) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }

        section.dispatchEvent(new CustomEvent('variant:change', { detail: { variant }, bubbles: true }));
      }
    }
  );

  /* Sticky add to cart (mobile) ------------------------------------------------ */
  define(
    'sticky-atc',
    class extends HTMLElement {
      connectedCallback() {
        const section = this.closest('[data-product-section]');
        const target = section?.querySelector('[data-main-atc]');
        if (!target || !('IntersectionObserver' in window)) return;
        const footer = document.querySelector('.section-footer');
        let atcVisible = true;
        let footerVisible = false;
        const update = () => {
          const show = !atcVisible && !footerVisible && target.getBoundingClientRect().top < 0;
          this.classList.toggle('is-visible', show);
          this.setAttribute('aria-hidden', show ? 'false' : 'true');
          this.querySelectorAll('button').forEach((b) => (b.tabIndex = show ? 0 : -1));
        };
        new IntersectionObserver(([entry]) => {
          atcVisible = entry.isIntersecting;
          update();
        }).observe(target);
        if (footer) {
          new IntersectionObserver(([entry]) => {
            footerVisible = entry.isIntersecting;
            update();
          }).observe(footer);
        }
      }
    }
  );

  /* Delivery estimate ------------------------------------------------------------ */
  define(
    'delivery-estimate',
    class extends HTMLElement {
      connectedCallback() {
        const min = Number(this.dataset.min);
        const max = Number(this.dataset.max);
        if (!min || !max) return;
        const addBusinessDays = (date, days) => {
          const d = new Date(date);
          let added = 0;
          while (added < days) {
            d.setDate(d.getDate() + 1);
            const day = d.getDay();
            if (day !== 0 && day !== 6) added++;
          }
          return d;
        };
        const today = new Date();
        const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'short', day: 'numeric' });
        const from = addBusinessDays(today, Math.min(min, max));
        const to = addBusinessDays(today, Math.max(min, max));
        this.textContent = fmt.formatRange ? fmt.formatRange(from, to) : `${fmt.format(from)} – ${fmt.format(to)}`;
      }
    }
  );
})();
